/* ============================================
   Restablecer — Crear / restablecer contraseña por token (público)
   Destino de los enlaces de: activación de cuenta tras la compra + "olvidé mi contraseña".
   Valida el token contra /auth/token-info y setea la clave con /auth/set-password.
   ============================================ */

import { useState, useEffect, type FormEvent } from 'react'
import LoginShell from '@/components/auth/LoginShell'
import { useNavigate, useSearchParams } from 'react-router-dom'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

export default function Restablecer() {
  const [params] = useSearchParams()
  const token = params.get('token') || ''
  const navigate = useNavigate()

  const [checking, setChecking] = useState(true)
  const [valid, setValid] = useState(false)
  const [email, setEmail] = useState('')
  const [tipo, setTipo] = useState<'activacion' | 'reset' | string>('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)

  useEffect(() => {
    let cancel = false
    async function check() {
      if (!token) { setChecking(false); setValid(false); return }
      try {
        const res = await fetch(`${API_URL}/api/auth/token-info/${encodeURIComponent(token)}`)
        const data = await res.json()
        if (cancel) return
        if (data.valido) { setValid(true); setEmail(data.email); setTipo(data.tipo) }
        else setValid(false)
      } catch {
        if (!cancel) setValid(false)
      } finally {
        if (!cancel) setChecking(false)
      }
    }
    check()
    return () => { cancel = true }
  }, [token])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (password.length < 8) { setError('La contraseña debe tener al menos 8 caracteres'); return }
    if (password !== confirm) { setError('Las contraseñas no coinciden'); return }
    setLoading(true)
    try {
      const res = await fetch(`${API_URL}/api/auth/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'No se pudo guardar la contraseña')
      setDone(true)
      setTimeout(() => navigate('/login', { replace: true }), 2500)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar la contraseña')
    } finally {
      setLoading(false)
    }
  }

  const esActivacion = tipo === 'activacion'
  const titulo = esActivacion ? 'Crea tu contraseña' : 'Restablece tu contraseña'

  if (checking) {
    return (
      <LoginShell pie={<>&copy; 2026 1Conecta</>}>
        <div className="login-panel__header"><h1>Verificando enlace…</h1></div>
        <div className="login-cargando"><span className="login-spinner" /></div>
      </LoginShell>
    )
  }

  if (!valid) {
    return (
      <LoginShell pie={<>&copy; 2026 1Conecta</>}>
        <div className="login-panel__header">
          <h1>Enlace no válido</h1>
          <p>Este enlace no es válido o ya expiró. Solicita uno nuevo desde «¿Olvidaste tu contraseña?».</p>
        </div>
        <button className="portal-login-submit" onClick={() => navigate('/login', { replace: true })}>Ir a iniciar sesión</button>
      </LoginShell>
    )
  }

  if (done) {
    return (
      <LoginShell pie={<>&copy; 2026 1Conecta</>}>
        <div className="login-panel__header">
          <h1>¡Listo!</h1>
          <p>Tu contraseña quedó guardada. Te llevamos a iniciar sesión…</p>
        </div>
        <button className="portal-login-submit" onClick={() => navigate('/login', { replace: true })}>Iniciar sesión</button>
      </LoginShell>
    )
  }

  return (
    <LoginShell pie={<>&copy; 2026 1Conecta</>}>
      <div className="login-panel__header">
          <h1>{titulo}</h1>
          <p>{esActivacion
            ? 'Crea la contraseña con la que entrarás a tu panel de cliente.'
            : 'Elige una nueva contraseña para tu cuenta.'}</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form" id="restablecer-form">
          {error && (
            <div className="login-error" role="alert">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="rp-email">Tu usuario (correo)</label>
            <input id="rp-email" type="email" value={email} readOnly disabled autoComplete="username" />
          </div>

          <div className="form-group">
            <label htmlFor="rp-password">Nueva contraseña</label>
            <input id="rp-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Mínimo 8 caracteres" required minLength={8} autoFocus autoComplete="new-password" />
          </div>

          <div className="form-group">
            <label htmlFor="rp-confirm">Confirmar contraseña</label>
            <input id="rp-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
              placeholder="Repite tu contraseña" required minLength={8} autoComplete="new-password" />
          </div>

          {password.length > 0 && (
            <ul className="login-reglas">
              <li className={password.length >= 8 ? 'is-ok' : 'is-mal'}>Mínimo 8 caracteres</li>
              {confirm.length > 0 && (
                <li className={password === confirm ? 'is-ok' : 'is-mal'}>Las contraseñas coinciden</li>
              )}
            </ul>
          )}

          <button type="submit" className="portal-login-submit"
            disabled={loading || password.length < 8 || password !== confirm} id="restablecer-submit">
            {loading ? <span className="login-spinner" /> : (esActivacion ? 'Crear contraseña y continuar' : 'Guardar contraseña')}
          </button>
        </form>

    </LoginShell>
  )
}
